import * as Y from 'yjs';

/**
 * Record bodies are Y.XmlFragments edited by TipTap (ProseMirror) through its Yjs binding.
 * This module writes ProseMirror-shaped JSON into a fragment (used by templates) and reads plain
 * text + record mentions back out (used by search and backlinks) without depending on TipTap.
 *
 * Encoding (same as the ProseMirror ↔ Yjs binding):
 *  - element node  → Y.XmlElement(nodeName = node type) with attributes = node attrs
 *  - text runs     → Y.XmlText, marks become formatting attributes `{ [markName]: markAttrs }`
 */

export interface RichMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface RichNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: RichNode[];
  text?: string;
  marks?: RichMark[];
}

/** Node type of an inline record mention (TipTap Mention extension). */
export const MENTION_NODE = 'mention';

/** Node type of a picture placed in a body. It holds only the blob hash, so no record backs it. */
export const IMAGE_NODE = 'assetImage';

/**
 * Always returns an object: `Y.Text.insert` without attributes *inherits* the formatting of the
 * previous character, so plain runs must pass `{}` to end a bold/link/highlight run.
 */
function marksToAttributes(marks: RichMark[] | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const m of marks ?? []) out[m.type] = m.attrs ?? {};
  return out;
}

function writeChildren(parent: Y.XmlFragment | Y.XmlElement, nodes: RichNode[]) {
  let textRun: Y.XmlText | null = null;
  for (const node of nodes) {
    if (node.type === 'text') {
      if (!node.text) continue;
      if (!textRun) {
        textRun = new Y.XmlText();
        parent.insert(parent.length, [textRun]);
      }
      textRun.insert(textRun.length, node.text, marksToAttributes(node.marks));
      continue;
    }
    textRun = null;
    const el = new Y.XmlElement(node.type);
    parent.insert(parent.length, [el]);
    for (const [key, value] of Object.entries(node.attrs ?? {})) {
      if (value !== null && value !== undefined) el.setAttribute(key, value as string);
    }
    if (node.content?.length) writeChildren(el, node.content);
  }
}

/** Append `nodes` (top-level blocks) to an integrated fragment. Call inside a transaction. */
export function writeRich(fragment: Y.XmlFragment, nodes: RichNode[]): void {
  writeChildren(fragment, nodes);
}

const BLOCK_BREAK = new Set(['paragraph', 'heading', 'listItem', 'taskItem', 'blockquote', 'codeBlock', 'callout', 'tableRow', 'horizontalRule']);

export interface BodyText {
  text: string;
  /** Record ids mentioned in the body, in order of appearance (may repeat). */
  mentions: string[];
  /** Blob hashes of the pictures placed in the body. */
  images: string[];
}

/** Plain text (with newlines between blocks) and mentioned record ids. */
export function readRichText(fragment: Y.XmlFragment | undefined): BodyText {
  const mentions: string[] = [];
  const images: string[] = [];
  let text = '';
  const walk = (node: Y.XmlFragment | Y.XmlElement) => {
    for (const child of node.toArray()) {
      if (child instanceof Y.XmlText) {
        for (const op of child.toDelta() as { insert: unknown }[]) if (typeof op.insert === 'string') text += op.insert;
      } else if (child instanceof Y.XmlElement) {
        if (child.nodeName === MENTION_NODE) {
          const id = child.getAttribute('id');
          if (typeof id === 'string') mentions.push(id);
          const label = child.getAttribute('label');
          if (typeof label === 'string') text += label;
          continue;
        }
        if (child.nodeName === IMAGE_NODE) {
          const hash = child.getAttribute('hash');
          if (typeof hash === 'string') images.push(hash);
          continue;
        }
        if (child.nodeName === 'hardBreak') {
          text += '\n';
          continue;
        }
        walk(child);
        if (BLOCK_BREAK.has(child.nodeName) && !text.endsWith('\n')) text += '\n';
      }
    }
  };
  if (fragment) walk(fragment);
  return { text: text.trim(), mentions, images };
}

/* ----------------------------------------------------------------------------------------------
 * Tiny builder DSL for templates:  doc(h(2, 'Overview'), p('Deals ', b('fire'), ' damage'))
 * -------------------------------------------------------------------------------------------- */

type Inline = string | RichNode;

const inline = (parts: Inline[]): RichNode[] => parts.map((x) => (typeof x === 'string' ? { type: 'text', text: x } : x));

export const rich = {
  p: (...parts: Inline[]): RichNode => ({ type: 'paragraph', content: inline(parts) }),
  h: (level: 1 | 2 | 3, ...parts: Inline[]): RichNode => ({ type: 'heading', attrs: { level }, content: inline(parts) }),
  b: (text: string): RichNode => ({ type: 'text', text, marks: [{ type: 'bold' }] }),
  i: (text: string): RichNode => ({ type: 'text', text, marks: [{ type: 'italic' }] }),
  code: (text: string): RichNode => ({ type: 'text', text, marks: [{ type: 'code' }] }),
  hl: (text: string): RichNode => ({ type: 'text', text, marks: [{ type: 'highlight' }] }),
  link: (text: string, href: string): RichNode => ({ type: 'text', text, marks: [{ type: 'link', attrs: { href, target: '_blank', rel: 'noopener noreferrer nofollow' } }] }),
  mention: (id: string, label: string): RichNode => ({ type: MENTION_NODE, attrs: { id, label } }),
  ul: (...items: (RichNode | string)[]): RichNode => ({
    type: 'bulletList',
    content: items.map((it) => (typeof it === 'string' ? { type: 'listItem', content: [rich.p(it)] } : it.type === 'listItem' ? it : { type: 'listItem', content: [it] })),
  }),
  ol: (...items: (RichNode | string)[]): RichNode => ({
    type: 'orderedList',
    attrs: { start: 1 },
    content: items.map((it) => (typeof it === 'string' ? { type: 'listItem', content: [rich.p(it)] } : it.type === 'listItem' ? it : { type: 'listItem', content: [it] })),
  }),
  li: (...blocks: RichNode[]): RichNode => ({ type: 'listItem', content: blocks }),
  todo: (...items: [boolean, ...Inline[]][]): RichNode => ({
    type: 'taskList',
    content: items.map(([checked, ...parts]) => ({ type: 'taskItem', attrs: { checked }, content: [rich.p(...parts)] })),
  }),
  quote: (...blocks: RichNode[]): RichNode => ({ type: 'blockquote', content: blocks }),
  callout: (tone: 'note' | 'idea' | 'warning' | 'danger' | 'success', ...blocks: RichNode[]): RichNode => ({ type: 'callout', attrs: { tone }, content: blocks }),
  codeBlock: (text: string, language = ''): RichNode => ({ type: 'codeBlock', attrs: { language }, content: [{ type: 'text', text }] }),
  hr: (): RichNode => ({ type: 'horizontalRule' }),
  table: (header: string[], ...rows: Inline[][]): RichNode => ({
    type: 'table',
    content: [
      { type: 'tableRow', content: header.map((h) => ({ type: 'tableHeader', attrs: { colspan: 1, rowspan: 1 }, content: [rich.p(h)] })) },
      ...rows.map((cells) => ({ type: 'tableRow', content: cells.map((c) => ({ type: 'tableCell', attrs: { colspan: 1, rowspan: 1 }, content: [rich.p(c)] })) })),
    ],
  }),
};
